'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useUserResourceSource } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import {
  AnonymousFallback,
  ButtonSkeleton,
  IslandKeyframes,
  ToggleErrorCaption,
  toggleActive,
  toggleError,
  toggleInactive,
} from './_shared';
import { useToggleErrorMessage } from './useToggleErrorMessage';

export interface SubscribeButtonProps {
  channelSlug: string;
  signInHref?: string;
}

export function SubscribeButton({ channelSlug, signInHref }: SubscribeButtonProps) {
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const { t } = useTranslation('site-builder-blocks');
  const { error, setError, clearError } = useToggleErrorMessage();

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.subscriptions) return null;
      return (await ur.subscriptions.isSubscribed(channelSlug, signal)) ?? false;
    },
    [user, ur, channelSlug],
  );

  if (!user)
    return (
      <>
        <IslandKeyframes />
        <AnonymousFallback
          variant="button"
          prompt={t('islands.signInToSubscribe')}
          {...(signInHref ? { signInHref } : {})}
        />
      </>
    );
  if (!ur?.subscriptions) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <ButtonSkeleton width="140px" />
      </>
    );

  const subbed = optimistic ?? (state.kind === 'loaded' ? state.data : false);
  const handle = async () => {
    if (toggling) return;
    clearError();
    setToggling(true);
    setOptimistic(!subbed);
    try {
      const res = await ur.subscriptions!.toggle({ channelSlug });
      if (!res || res.skipped) setOptimistic(null);
      else setOptimistic(Boolean(res.active));
    } catch (err) {
      // Roll back optimistic state and surface the failure inline.
      setOptimistic(null);

      console.error('[SubscribeButton] toggle failed', err);
      setError(t('islands.toggleFailed'));
    } finally {
      setToggling(false);
    }
  };

  if (state.kind === 'error')
    return (
      <button type="button" onClick={() => location.reload()} style={toggleError}>
        {t('islands.subscribe.retry')}
      </button>
    );

  return (
    <>
      <button
        type="button"
        onClick={handle}
        disabled={toggling}
        aria-pressed={subbed}
        style={subbed ? toggleActive : toggleInactive}
      >
        {subbed ? t('islands.subscribe.subscribed') : t('islands.subscribe.subscribe')}
      </button>
      {error && <ToggleErrorCaption message={error} />}
    </>
  );
}
